import {isNil} from 'ramda';

import type {RentalCalendarEventResponseBody} from '@dms/api/rental';
import type {MoneyResponseBody} from '@dms/api/shared';
import type {RentalCalendarVehicleResponseBody} from '@dms/api/vehicle';

import {Nullish, parseDate} from 'shared/web';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import type {RentalCalendarMoney} from '../types/RentalResourceCalendarRentalOrder';

/**
 * Maps calendar feed events to the UI event model: parses ISO datetimes into Dates, converts
 * decimal-string money into numbers and joins vehicle title/plate from the vehicle rows.
 *
 * A rental bar spans the actual handover interval when it exists (`vehicleRent`/`vehicleReturn`)
 * and falls back to the scheduled interval, mirroring the former grid mapping. Events without
 * a parsable start are dropped; an event without a usable end renders as a zero-length bar.
 */
export function mapCalendarEventsFromApi(
  events: RentalCalendarEventResponseBody[] | Nullish,
  vehicles: RentalCalendarVehicleResponseBody[] | Nullish
): RentalResourceCalendarEvent[] {
  const vehiclesById = new Map((vehicles ?? []).map((vehicle) => [vehicle.vehicleId, vehicle]));

  return (events ?? []).reduce<RentalResourceCalendarEvent[]>((result, event) => {
    const isBlock = event.type === 'block';

    const start = isBlock
      ? toValidDate(event.scheduledRent)
      : (toValidDate(event.vehicleRent) ?? toValidDate(event.scheduledRent));

    if (isNil(start)) {
      return result;
    }

    // A late pickup can leave `scheduledReturn` before the actual `vehicleRent`. An inverted
    // interval is dropped by the range filter and drawn backwards, so it collapses to a point.
    const rawEnd = isBlock
      ? toValidDate(event.scheduledReturn)
      : (toValidDate(event.vehicleReturn) ?? toValidDate(event.scheduledReturn));
    const end = isNil(rawEnd) || rawEnd < start ? parseDate(start) : rawEnd;

    const vehicle = vehiclesById.get(event.vehicleId);

    result.push({
      id: event.id,
      type: event.type,
      vehicleId: event.vehicleId,
      start,
      end,
      block: isBlock
        ? {
            reasonId: event.reasonId ?? undefined,
            vehicleTitle: vehicle?.title,
            licensePlate: vehicle?.registrationPlate ?? undefined,
          }
        : undefined,
      rentalOrder: isBlock
        ? undefined
        : {
            number: event.number ?? undefined,
            vehicleTitle: vehicle?.title,
            vehicleId: event.vehicleId,
            customerName: event.customerName ?? undefined,
            dailyRate: toUiMoney(event.dailyRate),
            expectedPrice: toUiMoney(event.expectedPrice),
            expectedPriceWithVat: toUiMoney(event.expectedPriceWithVat),
            salePrice: toUiMoney(event.salePrice),
            salePriceWithVat: toUiMoney(event.salePriceWithVat),
            licensePlate: vehicle?.registrationPlate ?? undefined,
            vin: event.vin ?? undefined,
            state: event.state ?? undefined,
            rentalLength: event.rentalLength ?? undefined,
            address: event.address ?? undefined,
            returnAddress: event.returnAddress ?? undefined,
            rentMileage: event.rentMileage ?? undefined,
            returnMileage: event.returnMileage ?? undefined,
          },
    });

    return result;
  }, []);
}

const toUiMoney = (money: MoneyResponseBody | Nullish): RentalCalendarMoney | null => {
  if (isNil(money)) {
    return null;
  }

  const amount = parseFloat(money.amount);

  return isNaN(amount) ? null : {amount, currency: money.currency};
};

/** `parseDate` can return an Invalid Date for malformed input — normalize that to null */
const toValidDate = (value: string | Nullish): Date | null => {
  if (isNil(value)) {
    return null;
  }

  const date = parseDate(value);

  return isNil(date) || isNaN(date.getTime()) ? null : date;
};
